import { useState, useEffect, useCallback } from 'react'
import api from '../services/api'
import toast from 'react-hot-toast'

/**
 * Panel lateral del inbox para buscar y enviar fichas de producto al chat
 */
export default function CatalogoPicker({ chatId, sesionId, onClose }) {
  const [q, setQ] = useState('')
  const [productos, setProductos] = useState([])
  const [buscando, setBuscando] = useState(false)
  const [enviando, setEnviando] = useState(null)
  const [detalle, setDetalle] = useState(null)

  const buscar = useCallback(async (query) => {
    if (!query.trim()) { setProductos([]); return }
    setBuscando(true)
    try {
      const { data } = await api.get('/alphastock/catalog', { params: { q: query, limit: 12 } })
      setProductos(data.productos || data)
    } catch { setProductos([]) }
    finally { setBuscando(false) }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => buscar(q), 300)
    return () => clearTimeout(t)
  }, [q, buscar])

  // Cargar primeros productos al abrir
  useEffect(() => { buscar(' ') }, [buscar])

  const stockColor = (s) => {
    if (s === 'available') return { bg: '#f0fdf4', color: '#16a34a', label: '✅ En stock' }
    if (s === 'low_stock')  return { bg: '#fefce8', color: '#ca8a04', label: '⚠️ Poco stock' }
    return { bg: '#fef2f2', color: '#dc2626', label: '❌ Agotado' }
  }

  const enviarProducto = async (producto) => {
    setEnviando(producto.sku)
    try {
      const precio = Number(producto.precio).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
      const st = stockColor(producto.stockStatus)
      const msg = [
        `📦 *${producto.nombre}*`,
        producto.descripcion ? `\n${producto.descripcion.slice(0, 120)}${producto.descripcion.length > 120 ? '...' : ''}` : '',
        `\n💰 Precio: *${precio}*`,
        `📊 Stock: ${st.label}`,
        producto.sku ? `🔖 SKU: ${producto.sku}` : '',
        producto.categoria ? `📂 Categoría: ${producto.categoria}` : '',
      ].filter(Boolean).join('\n')

      await api.post('/mensajes', { chatId, texto: msg })
      toast.success(`Ficha de "${producto.nombre}" enviada`)
    } catch {
      toast.error('Error al enviar')
    } finally { setEnviando(null) }
  }

  const verDetalle = async (sku) => {
    try {
      const [prod, stock] = await Promise.all([
        api.get(`/alphastock/catalog/${sku}`),
        api.get(`/alphastock/stock/${sku}`).catch(() => ({ data: null })),
      ])
      setDetalle({ ...prod.data, stockRealtime: stock.data })
    } catch { toast.error('No se pudo cargar el producto') }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>📦 Catálogo</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)' }}>✕</button>
      </div>

      {/* Search */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="🔍 Buscar producto..."
          style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none', background: 'var(--bg)' }} />
      </div>

      {/* Detalle modal */}
      {detalle && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'white', overflow: 'auto' }}>
          <div style={{ padding: 16 }}>
            <button onClick={() => setDetalle(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontWeight: 700, marginBottom: 12, fontSize: 13 }}>
              ← Volver
            </button>
            {detalle.imagenes?.[0] && (
              <img src={detalle.imagenes[0]} alt="" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, marginBottom: 12 }} />
            )}
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>{detalle.nombre}</div>
            {detalle.descripcion && <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 10, lineHeight: 1.5 }}>{detalle.descripcion}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Precio</div>
                <div style={{ fontWeight: 700 }}>{Number(detalle.precio).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}</div>
              </div>
              <div style={{ background: stockColor(detalle.stockStatus).bg, borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Stock</div>
                <div style={{ fontWeight: 700, color: stockColor(detalle.stockStatus).color }}>{stockColor(detalle.stockStatus).label}</div>
              </div>
              {detalle.stockRealtime?.available !== undefined && (
                <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Disponible ahora</div>
                  <div style={{ fontWeight: 700 }}>{detalle.stockRealtime.available} unidades</div>
                </div>
              )}
              {detalle.sku && (
                <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>SKU</div>
                  <div style={{ fontWeight: 700, fontFamily: 'monospace' }}>{detalle.sku}</div>
                </div>
              )}
            </div>
            {detalle.variantes?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Variantes</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {detalle.variantes.map((v, i) => (
                    <span key={i} style={{ padding: '4px 10px', background: 'var(--bg)', borderRadius: 20, fontSize: 12, border: '1px solid var(--border)' }}>
                      {v.nombre || v.sku}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <button className="btn btn-primary" style={{ width: '100%' }}
              onClick={() => { enviarProducto(detalle); setDetalle(null) }}
              disabled={enviando === detalle.sku}>
              {enviando === detalle.sku ? '⏳ Enviando...' : '📤 Enviar ficha al chat'}
            </button>
          </div>
        </div>
      )}

      {/* Lista de productos */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
        {buscando && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Buscando...</div>}

        {!buscando && productos.length === 0 && q && (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>
            Sin resultados para "{q}"
          </div>
        )}

        {productos.map(p => {
          const st = stockColor(p.stockStatus)
          return (
            <div key={p.sku || p.id}
              style={{ background: 'white', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', gap: 10, padding: 10 }}>
                {p.imagenes?.[0] ? (
                  <img src={p.imagenes[0]} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 54, height: 54, background: 'var(--bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>📦</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nombre}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)', marginBottom: 4 }}>
                    {Number(p.precio).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 20 }}>
                    {st.label}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', borderTop: '1px solid var(--border)' }}>
                <button onClick={() => verDetalle(p.sku)}
                  style={{ flex: 1, padding: '7px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                  👁 Ver detalle
                </button>
                <div style={{ width: 1, background: 'var(--border)' }} />
                <button onClick={() => enviarProducto(p)} disabled={enviando === p.sku}
                  style={{ flex: 1, padding: '7px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary)', fontWeight: 700 }}>
                  {enviando === p.sku ? '⏳...' : '📤 Enviar'}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
